/**
 * ═══════════════════════════════════════════════════════════════
 * WASP Frontend v2.0 - Response Rate Gauge Component
 * ═══════════════════════════════════════════════════════════════
 * 
 * @file components/ResponseRateGauge.tsx
 * @version 1.0.0
 * @description Gauge circular tipo velocímetro para tasa de respuesta
 * @created 2025-10-30 - Fase 2 Session 1
 * 
 * FEATURES:
 * - Gauge semicircular con SVG nativo
 * - Animación suave del arco y aguja
 * - Colores según porcentaje (rojo/naranja/verde)
 * - Número central grande animado
 * - Indicador de objetivo opcional
 * - Comparativa con periodo anterior
 * - Marcadores de escala (0%, 50%, 100%)
 * - Responsive (tamaño adaptativo)
 * - Tooltip informativo
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowUp,
  faArrowDown,
  faMinus,
  faBullseye
} from '@fortawesome/free-solid-svg-icons';

// Types
import {
  ResponseRateGaugeProps,
  PeriodComparison
} from '../types/performance_types';

// ═══════════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════════

const GAUGE_CONFIG = {
  size: 280,
  strokeWidth: 24,
  startAngle: -90,
  endAngle: 90,
  animationDuration: 1500, // ms
  needleLength: 0.65 // % del radio
};

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

/**
 * Obtiene el color según el porcentaje
 */
const getColorByValue = (value: number): string => {
  if (value < 40) return '#dc3545'; // Rojo (malo)
  if (value < 70) return '#fd7e14'; // Naranja (regular)
  return '#198754'; // Verde (bueno)
};

/**
 * Convierte grados a radianes
 */
const degreesToRadians = (degrees: number): number => {
  return (degrees * Math.PI) / 180;
};

/**
 * Calcula punto en el círculo
 */
const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number
): { x: number; y: number } => {
  const angleInRadians = degreesToRadians(angleInDegrees - 90);
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians)
  };
};

/**
 * Crea path de arco SVG
 */
const describeArc = (
  x: number,
  y: number,
  radius: number,
  startAngle: number,
  endAngle: number
): string => {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  return [
    'M', start.x, start.y,
    'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y
  ].join(' ');
};

/**
 * Calcula el ángulo de la aguja según el valor
 */
const calculateNeedleAngle = (value: number): number => {
  const { startAngle, endAngle } = GAUGE_CONFIG;
  const range = endAngle - startAngle;
  const angle = startAngle + (range * (value / 100));
  return angle;
};

/**
 * Obtiene el icono de tendencia
 */
const getTrendIcon = (trend: 'up' | 'down' | 'neutral') => {
  switch (trend) {
    case 'up': return faArrowUp;
    case 'down': return faArrowDown;
    case 'neutral': return faMinus;
    default: return faMinus;
  }
};

/**
 * Obtiene el color de tendencia
 */
const getTrendColor = (trend: 'up' | 'down' | 'neutral'): string => {
  switch (trend) {
    case 'up': return '#198754';
    case 'down': return '#dc3545';
    case 'neutral': return '#6c757d';
    default: return '#6c757d';
  }
};

// ═══════════════════════════════════════════════════════════════
// GAUGE COMPONENT
// ═══════════════════════════════════════════════════════════════

const ResponseRateGauge: React.FC<ResponseRateGaugeProps> = ({
  value,
  target,
  size = GAUGE_CONFIG.size,
  animated = true,
  comparison
}) => {

  // ─────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────

  const [displayValue, setDisplayValue] = useState<number>(0);
  const [currentAngle, setCurrentAngle] = useState<number>(GAUGE_CONFIG.startAngle);
  const animationRef = useRef<number | null>(null);

  // ─────────────────────────────────────────────────────────────
  // CALCULATIONS
  // ─────────────────────────────────────────────────────────────

  const center = size / 2;
  const radius = (size - GAUGE_CONFIG.strokeWidth * 2) / 2;
  const needleLength = radius * GAUGE_CONFIG.needleLength;
  
  const clampedValue = Math.max(0, Math.min(100, value));
  const targetAngle = target ? calculateNeedleAngle(target) : null;
  const valueColor = getColorByValue(clampedValue);

  // ─────────────────────────────────────────────────────────────
  // ANIMATIONS
  // ─────────────────────────────────────────────────────────────

  /**
   * Anima el valor numérico
   */
  useEffect(() => {
    if (!animated) {
      setDisplayValue(clampedValue);
      return;
    }

    const duration = GAUGE_CONFIG.animationDuration;
    const startTime = Date.now();
    const startValue = displayValue;
    const targetValue = clampedValue;
    const valueChange = targetValue - startValue;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out)
      const eased = 1 - Math.pow(1 - progress, 3);
      const newValue = startValue + (valueChange * eased);
      
      setDisplayValue(newValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [clampedValue, animated]);

  /**
   * Anima la aguja
   */
  useEffect(() => {
    if (!animated) {
      setCurrentAngle(calculateNeedleAngle(clampedValue));
      return;
    }

    const duration = GAUGE_CONFIG.animationDuration;
    const startTime = Date.now();
    const startAngle = currentAngle;
    const targetAngle = calculateNeedleAngle(clampedValue);
    const angleChange = targetAngle - startAngle;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out)
      const eased = 1 - Math.pow(1 - progress, 3);
      const newAngle = startAngle + (angleChange * eased);
      
      setCurrentAngle(newAngle);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [clampedValue, animated]);

  // ─────────────────────────────────────────────────────────────
  // SVG PATHS
  // ─────────────────────────────────────────────────────────────

  // Arco background (gris)
  const backgroundArc = describeArc(
    center,
    center,
    radius,
    GAUGE_CONFIG.startAngle,
    GAUGE_CONFIG.endAngle
  );

  // Arco de progreso (coloreado)
  const progressEndAngle = GAUGE_CONFIG.startAngle + 
    ((GAUGE_CONFIG.endAngle - GAUGE_CONFIG.startAngle) * (displayValue / 100));
  const progressArc = describeArc(
    center,
    center,
    radius,
    GAUGE_CONFIG.startAngle,
    progressEndAngle
  );

  // Posición de la aguja
  const needleEnd = polarToCartesian(center, center, needleLength, currentAngle);

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────

  return (
    <Card style={{
      border: 'none',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      backgroundColor: 'var(--bs-body-bg)',
      height: '100%'
    }}>
      <Card.Body className="d-flex flex-column align-items-center justify-content-center p-4">
        
        {/* Título */}
        <div className="mb-3 text-center">
          <h5 className="mb-1" style={{ fontWeight: '600' }}>
            Tasa de Respuesta
          </h5>
          {comparison && (
            <Badge 
              bg="light"
              text="dark"
              style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                padding: '0.375rem 0.625rem',
                border: `1px solid ${getTrendColor(comparison.trend)}20`,
                color: getTrendColor(comparison.trend)
              }}>
              <FontAwesomeIcon 
                icon={getTrendIcon(comparison.trend)} 
                size="xs"
                className="me-1"
              />
              {Math.abs(comparison.changePercent).toFixed(1)}%
            </Badge>
          )}
        </div>

        {/* SVG Gauge */}
        <svg 
          width={size} 
          height={size * 0.65}
          viewBox={`0 0 ${size} ${size * 0.65}`}
          style={{ overflow: 'visible' }}>
          
          {/* Arco background */}
          <path
            d={backgroundArc}
            fill="none"
            stroke="var(--bs-border-color)"
            strokeWidth={GAUGE_CONFIG.strokeWidth}
            strokeLinecap="round"
          />

          {/* Arco de progreso */}
          <path
            d={progressArc}
            fill="none"
            stroke={valueColor}
            strokeWidth={GAUGE_CONFIG.strokeWidth}
            strokeLinecap="round"
            style={{
              transition: animated ? 'stroke 0.3s' : 'none'
            }}
          />

          {/* Marcadores de escala */}
          {[0, 50, 100].map(mark => {
            const angle = GAUGE_CONFIG.startAngle + 
              ((GAUGE_CONFIG.endAngle - GAUGE_CONFIG.startAngle) * (mark / 100));
            const outerPoint = polarToCartesian(center, center, radius + 10, angle);
            const innerPoint = polarToCartesian(center, center, radius - 10, angle);
            
            return (
              <g key={mark}>
                {/* Línea del marcador */}
                <line
                  x1={innerPoint.x}
                  y1={innerPoint.y}
                  x2={outerPoint.x}
                  y2={outerPoint.y}
                  stroke="var(--bs-secondary)"
                  strokeWidth="2"
                  opacity="0.5"
                />
                {/* Texto del marcador */}
                <text
                  x={polarToCartesian(center, center, radius + 25, angle).x}
                  y={polarToCartesian(center, center, radius + 25, angle).y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="var(--bs-secondary)"
                  fontSize="12"
                  fontWeight="500">
                  {mark}%
                </text>
              </g>
            );
          })}

          {/* Indicador de objetivo (si existe) */}
          {targetAngle !== null && (
            <g>
              <line
                x1={center}
                y1={center}
                x2={polarToCartesian(center, center, radius - 5, targetAngle).x}
                y2={polarToCartesian(center, center, radius - 5, targetAngle).y}
                stroke="#0d6efd"
                strokeWidth="2"
                strokeDasharray="4,4"
                opacity="0.6"
              />
              <FontAwesomeIcon
                icon={faBullseye}
                style={{
                  position: 'absolute',
                  left: polarToCartesian(center, center, radius + 15, targetAngle).x - 8,
                  top: polarToCartesian(center, center, radius + 15, targetAngle).y - 8,
                  color: '#0d6efd',
                  fontSize: '16px'
                }}
              />
            </g>
          )}

          {/* Aguja */}
          <g style={{ transition: animated ? 'transform 1.5s ease-out' : 'none' }}>
            {/* Línea de la aguja */}
            <line
              x1={center}
              y1={center}
              x2={needleEnd.x}
              y2={needleEnd.y}
              stroke={valueColor}
              strokeWidth="3"
              strokeLinecap="round"
            />
            {/* Círculo central */}
            <circle
              cx={center}
              cy={center}
              r="8"
              fill={valueColor}
            />
            <circle
              cx={center}
              cy={center}
              r="4"
              fill="var(--bs-body-bg)"
            />
          </g>

          {/* Valor central */}
          <text
            x={center}
            y={center + 40}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="var(--bs-body-color)"
            fontSize="48"
            fontWeight="700"
            letterSpacing="-0.02em">
            {Math.round(displayValue)}
            <tspan fontSize="28" fill="var(--bs-secondary)">%</tspan>
          </text>

        </svg>

        {/* Info adicional */}
        {comparison && (
          <div className="mt-3 text-center">
            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
              {comparison.change > 0 ? '+' : ''}
              {comparison.change.toFixed(1)}% vs periodo anterior
            </small>
          </div>
        )}

        {/* Objetivo */}
        {target && (
          <div className="mt-2 text-center">
            <small style={{ 
              fontSize: '0.75rem',
              color: '#0d6efd',
              fontWeight: '600'
            }}>
              <FontAwesomeIcon icon={faBullseye} className="me-1" />
              Objetivo: {target}%
            </small>
          </div>
        )}

      </Card.Body>
    </Card>
  );
};

export default ResponseRateGauge;